<template>
  <div
    v-click-outside="closePickerCalendar"
    class="otb-date-picker"
    :class="requestClass"
    tabindex="0"
    role="button"
    :aria-expanded="showDatePicker"
    aria-roledescription="datepicker"
    @keyup.enter="togglePickerCalendar()"
    @keyup.esc="togglePickerCalendar()"
    @keyup.backspace="toggleClearDateButton"
    @keydown.space="preventDefaultBehaviour"
  >
    <label class="otb-date-picker__label wb-type-copy" v-if="formLabel">{{
      formLabel
    }}</label>
    <div class="otb-date-picker-input-group" :class="supportContentFilled()">
      <wb-input-control
        ref="inputControl"
        class="otb-date-picker__custom-input--pointer"
        :theme="theme"
        @click="preventDefaultBehaviour"
      >
        <wb-input class="otb-date-picker-input">
          <label class="otb-date-picker__input-label">{{
            labelPlaceholder
          }}</label>
          <input
            class="otb-date-picker__input"
            type="date"
            @input="inputDateWithDebounce"
            :value="inputFormattedDate"
            placeholder="dd/mm/yyyy"
            readonly
          />
          <button
            type="button"
            aria-label="Close Button"
            class="wb-round-button wb-round-button--small otb-date-picker__clear-button"
            @click="toggleClearDateButton"
            v-if="displayClearDateButton"
          >
            <wb-icon
              class="wb-round-button__icon"
              aria-hidden="true"
              name="close"
            ></wb-icon>
          </button>
        </wb-input>
      </wb-input-control>
      <slot name="supportContent"></slot>
    </div>

    <calendar
      class="otb-date-picker__calendar"
      :rangeMode="rangePicker"
      :selectedDates="selectedDatesValue"
      @changeDate="handleChangeDate"
      @closeMobileCalendar="closeMobileCalendar"
      ref="calendar"
      :activeCalendarPage="activeTextCarouselSlide"
      :startDate="calendarStartDate"
      :rowsInSheet="rowsInSheet"
      :locale="locale"
      :pages="calendarSheets"
      :dealerCalendar="dealerCalendar"
      v-show="datePickerVisibility"
      :weekStartsOn="weekStartsOn"
      :calendarType="calendarType"
    >
      <template v-slot:default="{ availableSheets }">
        <text-carousel
          class="otb-calendar-value wb-type-copy-secondary"
          :activeSlide="activeTextCarouselSlide"
          :infinite="false"
          :numberOfSlides="calendarSheets"
          :options="availableSheets"
          :variant="2"
          @input="changeSheet"
        >
        </text-carousel>
      </template>
    </calendar>
  </div>
</template>
<script lang="ts">
import {
  defineComponent,
  onMounted,
  onBeforeUnmount,
  watch,
  ref,
  computed,
} from 'vue';

import VClickOutside from '../../directives/v-click-outside';

import {
  DateRangeMode,
  DatesAvailabilityType,
  DateWorkingMode,
} from '../../models/entities/calendar.model';

import useDatePicker from '../../composables/date-picker.composable';

import DatetimePickerService from '../../../../otbc/src/services/datetimePicker.service';
export default defineComponent({
  // @ts-ignore
  name: 'otb-date-picker',
  components: {
    Calendar: () => import('../calendar/index.vue'),
    TextCarousel: () => import('../text-carousel/index.vue'),
  },
  directives: {
    'click-outside': VClickOutside,
  },
  props: {
    activeTextCarouselSlide: Number,
    calendarSheets: {
      type: Number,
      default: 3,
    },
    calendarStartDate: {
      type: Date,
      default() {
        return new Date();
      },
    },
    dealerCalendar: {
      type: Object as () => DatesAvailabilityType,
    },
    formLabel: String,
    isMobile: Boolean,
    isOptional: Boolean,
    labelPlaceholder: String,
    locale: {
      type: String,
      default: 'de-DE',
    },
    pickerVisibility: Boolean,
    rangePicker: {
      type: Object as () => { enabled: boolean; workingMode: DateWorkingMode },
      default() {
        return { enabled: true, workingMode: DateWorkingMode.START };
      },
    },
    rowsInSheet: {
      type: Number,
      default: 6,
    },
    selectedDates: {
      type: Object as () => DateRangeMode,
      default() {
        return {};
      },
    },
    selectedDay: {
      type: Date,
      default() {
        return new Date();
      },
    },
    weekStartsOn: {
      type: String,
      default: 'sun',
    },
    requestMode: Boolean,
    calendarType: {
      type: String,
      default: 'normal',
    },
    theme: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit, refs, slots }) {
    const DatePicker = useDatePicker(props, { emit, refs, slots });
    let dateSelected = ref(false);

    const requestClass = computed(() => {
      return props.requestMode ? 'otb-date-picker__request' : '';
    });

    const displayClearDateButton = computed(() => {
      return props.isOptional && dateSelected.value;
    });

    // watchers
    watch(
      () => props.isMobile,
      (): void => {
        const clickableElements = DatePicker.getClickableElements();
        if (clickableElements) {
          DatePicker.addEventListeners(clickableElements);
        }
      }
    );

    // lifecycle
    onMounted((): void => {
      DatePicker.clickableElementsObserver();
      if (props.selectedDates && props.selectedDates.start) {
        dateSelected.value = true;
      }
    });

    onBeforeUnmount((): void => {
      DatePicker.removeEventListeners();
    });

    /**
     * Prevent native WB input date calendar
     */
    const preventDefaultBehaviour = (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
    };

    const handleChangeDate = (value: Date) => {
      DatePicker.changeDate(value);
      dateSelected.value = true;
    };

    const toggleClearDateButton = () => {
      emit('changeDate', null);
      dateSelected.value = !dateSelected.value;
      DatetimePickerService.commitSelectedDay(null);
    };

    const closeMobileCalendar = () => {
      emit('closeMobileCalendar');
    };

    return {
      requestClass,
      ...DatePicker,
      preventDefaultBehaviour,
      toggleClearDateButton,
      dateSelected,
      displayClearDateButton,
      handleChangeDate,
      closeMobileCalendar,
    };
  },
});
</script>
<style src="./date-picker.scss" lang="scss"></style>
